import { useEffect, useState } from "react";
import { CalendarCheck, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAppointment } from "@/context/AppointmentContext";
import { SERVICE_CATEGORIES } from "@/data/services";
import { SITE, whatsappHref } from "@/data/site";

const ALL_SERVICES = SERVICE_CATEGORIES.flatMap((c) =>
  c.services.map((s) => `${c.title} — ${s.name}`)
);

const empty = { full_name: "", phone: "", email: "", service: "", preferred_date: "", message: "" };

export function AppointmentDialog() {
  const { open, setOpen, defaultService } = useAppointment();
  const [form, setForm] = useState(empty);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...empty, service: defaultService || "" });
      setDone(false);
    }
  }, [open, defaultService]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const buildWhatsAppMessage = () => {
    const lines = [
      "Hi UNIRAD DIAGNOSTICS, I'd like to book an appointment.",
      "",
      `Name: ${form.full_name}`,
      `Phone: ${form.phone}`,
    ];
    if (form.email) lines.push(`Email: ${form.email}`);
    if (form.service) lines.push(`Service: ${form.service}`);
    if (form.preferred_date) lines.push(`Preferred Date: ${form.preferred_date}`);
    if (form.message) lines.push("", `Message: ${form.message}`);
    return lines.join("\n");
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    window.open(whatsappHref(buildWhatsAppMessage()), "_blank", "noopener,noreferrer");
    setDone(true);
    toast.success("Opening WhatsApp to confirm your appointment.");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="appointment-dialog"
        className="max-w-lg rounded-2xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        <div className="bg-brand-navy px-6 py-5 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2 text-white">
              <CalendarCheck className="h-5 w-5 text-brand-sky" /> Book an Appointment
            </DialogTitle>
            <DialogDescription className="text-white/70">
              Fill in your details and our team will confirm your slot.
            </DialogDescription>
          </DialogHeader>
        </div>

        {done ? (
          <div data-testid="appointment-success" className="px-6 py-12 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
            <h3 className="mt-4 font-heading text-xl font-semibold text-brand-navy">Request Received!</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Thank you, {form.full_name.split(" ")[0]}. Send the WhatsApp message to complete your booking request.
            </p>
            <Button className="mt-6 rounded-full bg-booking hover:bg-booking-dark" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="ap-name">Full Name *</Label>
                <Input id="ap-name" data-testid="appointment-name" className="h-11" placeholder="Your name" value={form.full_name} onChange={set("full_name")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ap-phone">Phone Number *</Label>
                <Input id="ap-phone" data-testid="appointment-phone" className="h-11" placeholder="10-digit mobile" value={form.phone} onChange={set("phone")} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ap-email">Email</Label>
              <Input id="ap-email" type="email" data-testid="appointment-email" className="h-11" placeholder="you@email.com" value={form.email} onChange={set("email")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Service Required</Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="appointment-service" className="h-11">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {ALL_SERVICES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ap-date">Preferred Date</Label>
                <Input id="ap-date" type="date" data-testid="appointment-date" className="h-11" value={form.preferred_date} onChange={set("preferred_date")} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ap-msg">Message</Label>
              <Textarea id="ap-msg" data-testid="appointment-message" placeholder="Any additional details..." value={form.message} onChange={set("message")} rows={3} />
            </div>
            <Button
              type="submit"
              data-testid="appointment-submit"
              className="w-full h-12 rounded-full bg-booking hover:bg-booking-dark text-base shadow-md shadow-booking/25"
            >
              Continue on WhatsApp
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              You'll be redirected to WhatsApp ({SITE.phones[0].number})
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
